import { getAccountNumber, type TransformedCartData, transformEntries } from "./CartViewDataTransformer";
import {
  type B2BUnitDataDTO,
  type CheckoutViewDTO,
  type DeliveryModeDataDTO,
  type OrderType,
  type PaymentTypeDTO,
  type UnloadingPointDTO,
} from "../dto/CheckoutViewDTO";
import { type AddressData, type UnloadingPointData } from "~/feature/cart/checkout/components/Address";
import { adjustFormat } from "../../../../utils/accountNumberUtils";
import { type CartEntry, type LineItem } from "../models/CartData";
import { getNextUID } from "../../../../utils/uid";
import { groupByFn, toMapFn } from "~/utils/reducers";

export interface TransformedCheckoutData extends TransformedCartData {
  addresses: AddressData[];
  deliveryAddress: AddressData;
  soldTo: {
    name: string;
    accNo: string;
    addresses: AddressData[];
  };
  countriesList: { isocode: string; name: string }[];
  canAddAddress: boolean;
  unloadingPoints?: Dict<UnloadingPointData[]>;
  defaultCreditCard: CreditCardData | undefined;
  supportedPaymentTypes: SupportedPaymentsTypesData[];
  orderablePackageIds: number[];
  quotablePackageIds: number[];
}

export interface CreditCardData {
  defaultCard: boolean;
  expMonth: string;
  expYear: string;
  expired: boolean;
  number: string;
  type: string;
  customerRefNum?: string;
}

export interface SupportedPaymentsTypesData {
  code: string;
  deliveryModes: DeliveryModeData[];
}

export interface FreightPaymentsData {
  accountNumber: boolean;
  id: string;
  name: string;
}

export interface DeliveryModeData {
  code: string;
  name: string;
  freightPayments: FreightPaymentsData[];
  validOrderTypes: OrderType[];
  shippingConditionCodes: string[]; // translation key - Messages to be shown inside card. deliveryModeInfo in BE
  paymentCodeInfo?: string; // translation key - text to be shown in checkout summary as "freight charges" header. freightPayment in BE
  paymentCodeDisplay?: string; // translation key - text to be shown in checkout summary / payment. infoCode in BE
}

export function transform(dto?: CheckoutViewDTO): TransformedCheckoutData {
  if (!dto) {
    return {
      cartEntriesById: {},
      addresses: [],
      soldTo: {
        accNo: "",
        name: "",
        addresses: [],
      },
      accountNumber: "",
      cartEntries: [],
      packEntriesById: {},
      productByCode: {},
      countriesList: [{ isocode: "", name: "" }],
      canAddAddress: false,
      deliveryAddress: {
        id: "",
        companyName: "",
        lines: [],
        country: { isocode: "", name: "" },
        town: "",
        postalCode: "",
        state: "",
        default: false,
        editable: false,
        temporary: true,
      },
      dstError: false,
      defaultCreditCard: undefined,
      supportedPaymentTypes: [],
      orderablePackageIds: [],
      quotablePackageIds: [],
      comment: "",
    };
  }

  const addresses = (dto.deliveryAddressesList || []).map(transformAddress);

  let accNo = dto.soldTo.uid || "";
  accNo = adjustFormat(accNo);

  let deliveryAddress;
  if (dto.deliveryAddress) {
    const id = dto.deliveryAddress.id;
    deliveryAddress = addresses.find((address) => address.id === id) || transformAddress(dto.deliveryAddress);
  } else {
    deliveryAddress = addresses[0];
  }

  const soldToAddresses = (dto.soldTo.addresses || []).map(transformAddress);

  const accountNumber = getAccountNumber(dto.b2bCustomerData);

  const { cartEntries, packageEntries, productByCode, orderablePackageIds, quotablePackageIds } =
    transformCheckoutCart(dto);

  return {
    cartEntries: cartEntries.map((entry) => entry.id),
    cartEntriesById: toMapFn(cartEntries, (entry) => entry.id),
    packEntriesById: toMapFn(packageEntries, (entry) => entry.id),
    productByCode,
    accountNumber,
    addresses: addresses,
    soldTo: {
      name: dto.soldTo.name,
      accNo: accNo,
      addresses: soldToAddresses,
    },
    orderablePackageIds,
    quotablePackageIds,
    countriesList: dto.countriesList,
    canAddAddress: dto.soldTo.addAddress,
    deliveryAddress,
    unloadingPoints: transformUnloadingPointMap(dto.soldTo.children || []),
    defaultCreditCard: dto.defaultCreditCard,
    supportedPaymentTypes: (dto.supportedPaymentTypes ?? []).map(transformSupporterdPaymentTypes),
    dstError: dto.dstError,
    comment: "",
  };
}

function transformCheckoutCart(dto: CheckoutViewDTO) {
  const orderableData = transformEntries(dto.orderables);
  const quotableData = transformEntries(dto.quotables);

  const productsByCode = {
    ...orderableData.productByCode,
    ...quotableData.productByCode,
  };

  const cartEntriesByProductCode = groupByFn(
    [...orderableData.cartEntries, ...quotableData.cartEntries],
    (entry) => entry.productCode,
  );

  const packageEntriesByProductCode = groupByFn(
    [...orderableData.packageEntries, ...quotableData.packageEntries],
    (entry) => entry.productCode,
  );

  const cartEntryPerProductCode: Record<string, CartEntry> = {};
  // Merge cart entries
  Object.values(cartEntriesByProductCode).forEach((entries) => {
    const [first, ...rest] = entries;
    for (const entry of rest) {
      first.packEntryIds.push(...entry.packEntryIds);
    }
    cartEntryPerProductCode[first.productCode] = first;
  });

  // Adjust the cart entry id of package entries
  Object.values(packageEntriesByProductCode).forEach((entries) => {
    for (const entry of entries) {
      entry.cartEntryId = cartEntryPerProductCode[entry.productCode].id;
    }
  });

  return {
    cartEntries: Object.values(cartEntryPerProductCode).sort((a, b) => a.entryNumber - b.entryNumber),
    packageEntries: Object.values(packageEntriesByProductCode)
      .flat()
      .sort((a, b) => {
        const result = a.cartEntryNumber - b.cartEntryId;
        if (result === 0) {
          if (a.entryNumber === null && b.entryNumber === null) {
            return 0;
          }
          if (a.entryNumber === null) {
            return 1;
          }
          if (b.entryNumber === null) {
            return -1;
          }
          return a.entryNumber - b.entryNumber;
        }
        return result;
      }),
    productByCode: productsByCode,
    orderablePackageIds: orderableData.packageEntries.map((entry) => entry.id),
    quotablePackageIds: quotableData.packageEntries.map((entry) => entry.id),
  };
}

export function transformAddress(dto: AddressDTO): AddressData {
  const lines = dto.lines || [];

  if (dto.line1) {
    // Line 1 contains house number and street
    lines.push(dto.line1);
  }

  if (dto.additionalLine1) {
    // Additional address line (displayed after Line1)
    lines.push(dto.additionalLine1);
  }

  if (dto.line2) {
    // Line2 contains the city, region and postalcode
    lines.push(dto.line2);
  }

  return {
    id: dto.id,
    companyName: dto.companyName,
    lines,
    postalCode: dto.postalCode,
    town: dto.town,
    country: dto.country,
    default: dto.defaultAddress,
    editable: dto.editable,
    temporary: dto.temporary !== undefined ? dto.temporary : false,
    state: dto.province !== undefined ? dto.province : "",
    parentUnit: dto.parentUnit || "",
  };
}

export function transformUnloadingPointMap(dtos: B2BUnitDataDTO[]): Dict<UnloadingPointData[]> {
  const result: Dict<UnloadingPointData[]> = {};
  dtos.forEach((item) => (result[item.uid] = (item.unloadingPoints || []).map(transformUnloadingPoint)));
  return result;
}

export function transformUnloadingPoint(dto: UnloadingPointDTO): UnloadingPointData {
  return {
    id: dto.id,
    name: dto.name,
    selected: dto.selected,
  };
}

export function transformDeliveryMode(dto: DeliveryModeDataDTO): DeliveryModeData {
  return {
    code: dto.code,
    name: dto.name,
    freightPayments: dto.freightPayments,
    validOrderTypes: dto.orderTypes,
    shippingConditionCodes: dto.shippingConditionCodes,
    paymentCodeDisplay: dto.infoCode,
    paymentCodeInfo: dto.freightPayment,
  };
}

export function transformSupporterdPaymentTypes(dto: PaymentTypeDTO): SupportedPaymentsTypesData {
  return {
    ...dto,
    deliveryModes: dto.deliveryModes.map(transformDeliveryMode),
  };
}

/**
 * Function splits cart entries that have entries with and without prices into two separate entries, so it's easier
 * to render them later
 * @param data TransformedCartData
 */
function adjustForOEM(data: TransformedCartData) {
  for (const cartEntry of Object.values(data.cartEntriesById)) {
    const packEntries = cartEntry.packEntryIds.map((id) => data.packEntriesById[id]);
    const packagesWithPrice = packEntries.filter(packEntryWithPrice);
    const packagesWithoutPrice = packEntries.filter(packEntryWithoutPrice);

    if (packagesWithPrice.length > 0 && packagesWithoutPrice.length > 0) {
      cartEntry.packEntryIds = packagesWithPrice.map((entry) => entry.id);

      const cartEntryCopy = { ...cartEntry, id: getNextUID() };
      cartEntryCopy.packEntryIds = packagesWithoutPrice.map((entry) => entry.id);

      data.cartEntriesById[cartEntryCopy.id] = cartEntryCopy;
      data.cartEntries.push(cartEntryCopy.id);
    }
  }
}

export const packEntryWithPrice = (packEntry: LineItem) => packEntry.netPrice && packEntry.netPrice.value > 0;
export const packEntryWithoutPrice = (packEntry: LineItem) => !packEntryWithPrice(packEntry);
